import { type SubmissionResult } from '@conform-to/react'
import * as Popover from '@radix-ui/react-popover'
import { useFetcher } from '@remix-run/react'
import { type NodeViewProps } from '@tiptap/core'
import { NodeViewContent, NodeViewWrapper } from '@tiptap/react'
import {
	Asterisk,
	ChevronDown,
	CircleMinus,
	CirclePlus,
	MailCheck,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { SwitchField } from '#app/components/forms.js'
import { Button } from '#app/components/ui/button.js'
import { StatusButton } from '#app/components/ui/status-button.js'

export const Component = (props: NodeViewProps) => {
	const { editor } = props
	const editable = editor.isEditable
	const fetcher = useFetcher<SubmissionResult<string[]>>()
	const $form = useRef<HTMLFormElement>(null)
	const loading = fetcher.state === 'loading' || fetcher.state === 'submitting'
	const [emailError, setEmailError] = useState(fetcher.data?.error)
	const cta = props.node.attrs.cta
	const redirectUrl = props.node.attrs.redirectUrl
	const redirect = props.node.attrs.redirect

	useEffect(() => {
		if (fetcher.data && !fetcher.data.error && redirect) {
			window.parent.location.href = redirectUrl
		}
	}, [fetcher.data])

	useEffect(() => {
		if (fetcher.data?.error) {
			$form.current?.reset()
		}
		setEmailError(fetcher.data?.error)
		$form.current?.focus()
	}, [fetcher.data?.error])

	if (editable) {
		const addInput = () => {
			if (editor) {
				const nodeEndPos = props.getPos() + props.node.nodeSize

				editor
					.chain()
					.focus()
					.insertContentAt(nodeEndPos - 1, '<simple-input>Hello</simple-input>')
					.blur()
					.run()
			}
		}

		return (
			<NodeViewWrapper>
				<div className="flex w-[420px] max-w-full flex-col">
					<Popover.Root>
						<Popover.Trigger>
							<div className="relative">
								<div className="absolute inset-y-1/4 right-3">
									<Asterisk width={16} className="text-tt-input" />
								</div>
								<input
									placeholder="Email"
									value="Email"
									className="input-tt pointer-events-none w-full active:outline-none"
									disabled
								/>
							</div>
						</Popover.Trigger>
						<Popover.Portal>
							<Popover.Content
								className="custom-popover z-50 flex items-center gap-2 rounded-xl border border-border bg-white p-2 shadow-sm"
								sideOffset={8}
							>
								<div className="flex items-center gap-2">
									<div className="flex">
										<Button
											disabled
											data-tooltip="Remove input"
											data-tooltip-direction="up"
											className="flex h-[32px] w-[32px] items-center justify-center	 rounded-lg rounded-r-none border border-border bg-primary-foreground transition-all duration-150 ease-in-out group-hover:-rotate-6 group-hover:shadow-sm"
										>
											<CircleMinus width={16} className="shrink-0" />
										</Button>
										<Button
											onClick={addInput}
											data-tooltip="Add input"
											data-tooltip-direction="up"
											className="flex h-[32px] w-[32px] items-center justify-center rounded-lg rounded-l-none border border-l-0 border-border bg-primary-foreground transition-all duration-150 ease-in-out group-hover:-rotate-6 group-hover:shadow-sm"
										>
											<CirclePlus width={16} className="shrink-0" />
										</Button>
									</div>
									<SwitchField
										labelProps={{ children: 'Required' }}
										defaultChecked={true}
										className="pointer-events-none"
									/>
									<div className="relative w-32">
										<label
											htmlFor="variableSelect"
											data-tooltip="Connected field"
											data-tooltip-direction="up"
										>
											<select
												id="variableSelect"
												name="variable"
												disabled
												className="h-[32px] w-full appearance-none rounded-lg border border-border px-2 outline-none hover:outline-none active:outline-none"
												value={props.node.attrs.id}
												defaultValue={props.node.attrs.name}
											>
												<option key="email" value="Email">
													Email
												</option>
											</select>
										</label>
										<ChevronDown
											className="pointer-events-none absolute inset-y-1 right-1.5"
											width={16}
										/>
									</div>
								</div>
								{/* <Popover.Arrow className="fill-white" /> */}
							</Popover.Content>
						</Popover.Portal>
					</Popover.Root>
					<NodeViewContent className="mt-4 flex flex-col gap-4" />
					<StatusButton
						type="button"
						variant="customizer"
						status={loading ? 'pending' : 'idle'}
						className="button-tt pointer-events-none !flex"
					>
						<input
							defaultValue={cta}
							onChange={e =>
								props.updateAttributes({ cta: e.currentTarget.value })
							}
							className="button-text-tt tt-input-button w-full appearance-none !border-none !bg-transparent !text-center !text-sm !outline-none"
							type="text"
						/>
					</StatusButton>
				</div>
			</NodeViewWrapper>
		)
	} else {
		return (
			<NodeViewWrapper>
				<fetcher.Form
					method="POST"
					autoComplete="off"
					ref={$form}
					className={`relative mb-6 flex w-[420px] max-w-full flex-col items-center justify-center ${fetcher.data && !redirect ? 'border-tt' : 'border border-transparent'} rounded-lg`}
				>
					<div
						className={`z-50 ${fetcher.data && !redirect ? 'pointer-events-none opacity-0' : 'opacity-100'} flex w-full flex-col transition-all duration-100 ease-in-out`}
					>
						<div className="relative">
							<div className="absolute inset-y-1/4 right-3">
								<Asterisk width={16} className="text-tt-input" />
							</div>
							<input
								className="input-tt w-full appearance-none"
								placeholder="Email"
								name="email"
								type="email"
								required
							/>
						</div>
						<NodeViewContent className="mt-4 flex flex-col gap-4" />
						<StatusButton
							type="submit"
							variant="customizer"
							status={loading ? 'pending' : 'idle'}
							className="button-tt !flex"
						>
							{props.node.attrs.cta}
						</StatusButton>
						{emailError?.toString()}
					</div>
					<div
						className={`${fetcher.data && !redirect ? 'opacity-100' : 'opacity-0'} absolute flex flex-col items-center justify-center gap-2 transition-all duration-100 ease-in-out`}
					>
						<MailCheck width={20} />
						<h3>Check your email</h3>
						<p className="!mt-0">We've sent you a verification email.</p>
					</div>
				</fetcher.Form>
			</NodeViewWrapper>
		)
	}
}

export default Component
